import JsCookie from 'js-cookie';
import { BizPlatform, DeployEnv } from './types';

export const RUNTIME_OVERRIDE_COOKIE_KEY = '__DEV_TOOL_ENV_TAG__';

const isRuntimeSourceString = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed !== '' && !trimmed.includes('<%') && !trimmed.includes('%>');
};

export function clearRuntimeOverrideCookie(): void {
  JsCookie.remove(RUNTIME_OVERRIDE_COOKIE_KEY);
}

export interface RuntimeSourceInput {
  readonly server: {
    readonly apiHost?: unknown;
    readonly platform?: unknown;
    readonly stage?: unknown;
  };
  readonly vite: {
    readonly apiHost?: unknown;
    readonly platform?: unknown;
    readonly stage?: unknown;
    readonly galileoTraceEnabled?: unknown;
    readonly isDev: boolean;
  };
  readonly location: {
    readonly hostname: string;
    readonly host: string;
    readonly origin: string;
    readonly protocol: string;
  };
  readonly proxyPrefix?: string;
  readonly targetOverride?: string | null;
}

function readSourceValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return isRuntimeSourceString(trimmed) ? trimmed : undefined;
}

export function hasRuntimeSourceValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;
  return isRuntimeSourceString(value);
}

function readWindowValue(key: '__SERVER_ASSIGN_API_HOST__' | '__SERVER_ASSIGN_BIZ_PLATFORM__' | '__SERVER_ASSIGN_DEPLOY_ENV__'): unknown {
  if (typeof window === 'undefined') return undefined;
  return window[key];
}

function readProxyPrefix(): string | undefined {
  return typeof __DEV_API_PROXY_PREFIX__ === 'string' && __DEV_API_PROXY_PREFIX__
    ? __DEV_API_PROXY_PREFIX__
    : undefined;
}

export function readRuntimeSources(): RuntimeSourceInput {
  const hostname = typeof location === 'undefined' ? '' : location.hostname;
  return {
    server: {
      apiHost: readSourceValue(readWindowValue('__SERVER_ASSIGN_API_HOST__')),
      platform: readSourceValue(readWindowValue('__SERVER_ASSIGN_BIZ_PLATFORM__')),
      stage: readSourceValue(readWindowValue('__SERVER_ASSIGN_DEPLOY_ENV__')),
    },
    vite: {
      apiHost: readSourceValue(import.meta.env.VITE_API_HOST),
      platform: readSourceValue(import.meta.env.VITE_BIZ_PLATFORM),
      stage: readSourceValue(import.meta.env.VITE_DEPLOY_ENV),
      galileoTraceEnabled: readSourceValue(import.meta.env.VITE_GALILEO_TRACE_ENABLED),
      isDev: Boolean(import.meta.env.DEV),
    },
    location: {
      hostname,
      host: typeof location === 'undefined' ? '' : location.host,
      origin: typeof location === 'undefined' ? '' : location.origin,
      protocol: typeof location === 'undefined' ? '' : location.protocol,
    },
    proxyPrefix: readProxyPrefix(),
    targetOverride: JsCookie.get(RUNTIME_OVERRIDE_COOKIE_KEY) || null,
  };
}

export function parsePlatform(value: unknown): BizPlatform | undefined {
  return value === BizPlatform.GCP || value === BizPlatform.SG ? value : undefined;
}

export function parseDeployEnv(value: unknown): DeployEnv | undefined {
  return Object.values(DeployEnv).includes(value as DeployEnv) ? value as DeployEnv : undefined;
}

export function normalizeApiHost(value: unknown): string | undefined {
  const raw = readSourceValue(value);
  if (typeof raw !== 'string') return undefined;
  if (!raw || raw.includes('/') || raw.includes('@')) return undefined;
  try {
    const parsed = new URL(`https://${raw}`);
    if (parsed.hostname !== raw && !raw.startsWith(`${parsed.hostname}:`)) return undefined;
    return raw;
  } catch {
    return undefined;
  }
}
